<template>
  <BaseDialog
    centered
    ref="dialog"
    contentClass="pre-project-form"
    :show-actions="true"
    @closeModal="close"
    :onClose="close"
  >
    <template slot="title">
      {{ $t(`page.pre-projects.form.${isEdit ? 'edit-title' : 'create-title'}`) }}
    </template>
    <template slot="content">
      <!-- Title (required) -->
      <div class="pre-project-form__field">
        <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.title-label') }} *</label>
        <FormInput
          name="pre-project-title"
          :value="form.title"
          :placeholder="$t('page.pre-projects.form.title-placeholder')"
          @input="form.title = $event"
        />
      </div>

      <!-- Locations (at least one): a pre-project can apply to several places.
           Province -> région -> ville: picking a city adds it to the list, and
           "Other" adds a custom place inside the picked region (customName).
           Each chip removes with its X. -->
      <div class="pre-project-form__field">
        <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.locations-label') }} *</label>
        <div v-if="form.locations.length" class="pre-project-form__locations">
          <BaseChip
            v-for="(location, index) in form.locations"
            :key="locationKey(location)"
            v-tooltip="{ content: locationTooltip(location), placement: 'top' }"
            size="sm"
            preset="soft"
            tone="info"
            clearable
            :interactive="false"
            :clear-label="$t('actions.delete')"
            @clear="removeLocation(index)"
          >
            {{ locationLabel(location) }}
          </BaseChip>
        </div>
        <p v-else class="pre-project-form__locations-hint">{{ $t('page.pre-projects.form.no-locations') }}</p>

        <div class="pre-project-form__row pre-project-form__row--stacked">
          <!-- Province: narrows the region list. Lookup helper only — a city
               pick carries its own id; a custom place is tied to its region. -->
          <div class="pre-project-form__field pre-project-form__field--inner">
            <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.province-label') }}</label>
            <FormSelect
              :value="selectedProvince"
              :options="provinces"
              idProp="id"
              labelProp="name"
              :placeholder="$t('page.pre-projects.form.province-placeholder')"
              @input="onProvinceSelected"
            />
          </div>
          <!-- Region (enabled once a province is picked): narrows the city lookup (?regionId=). -->
          <div
            class="pre-project-form__field pre-project-form__field--inner"
            :class="{ 'is-disabled': !selectedProvinceId }"
          >
            <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.region-label') }}</label>
            <FormSelect
              :value="selectedRegion"
              :options="regionsOfProvince"
              idProp="id"
              labelProp="name"
              :placeholder="
                selectedProvinceId
                  ? $t('page.pre-projects.form.region-placeholder')
                  : $t('page.pre-projects.form.region-requires-province')
              "
              @input="onRegionSelected"
            />
          </div>
        </div>

        <div class="pre-project-form__row">
          <!-- City (enabled once a region is picked). A city pick joins the list
               and the select clears, ready for the next one; "Other" opens the
               custom place input beside it. -->
          <div
            class="pre-project-form__field pre-project-form__field--inner"
            :class="{ 'is-disabled': !selectedRegionId }"
          >
            <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.city-label') }}</label>
            <FormSelect
              :value="customLocationMode ? otherCityOption : null"
              :options="cityOptions"
              idProp="id"
              labelProp="name"
              :clearOnSelect="!customLocationMode"
              :placeholder="
                selectedRegionId
                  ? $t('page.pre-projects.form.city-placeholder')
                  : $t('page.pre-projects.form.city-requires-region')
              "
              @input="onCitySelected"
            />
          </div>
          <!-- Custom place: a free-text name inside the picked region, added
               with the button or Enter (becomes the entry's customName). -->
          <div v-if="customLocationMode" class="pre-project-form__field pre-project-form__field--inner">
            <label class="pre-project-form__field-label">
              {{ $t('page.pre-projects.form.custom-location-label') }}
            </label>
            <div class="pre-project-form__custom-location">
              <FormInput
                name="pre-project-custom-location"
                :value="customLocationName"
                :placeholder="$t('page.pre-projects.form.custom-location-placeholder')"
                @input="customLocationName = $event"
                @keyup.native.enter="commitCustomLocation"
              />
              <BaseButton isSquare isOutlined :disabled="!isCustomLocationValid" @click.native="commitCustomLocation">
                {{ $t('page.pre-projects.form.add-location') }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>

      <div class="pre-project-form__row">
        <!-- Emitter (optional) -->
        <div class="pre-project-form__field">
          <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.emitter-label') }}</label>
          <FormInput name="pre-project-emitter" :value="form.emitter" @input="form.emitter = $event" />
        </div>
        <!-- Project number (optional) -->
        <div class="pre-project-form__field">
          <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.project-number-label') }}</label>
          <FormInput name="pre-project-number" :value="form.projectNumber" @input="form.projectNumber = $event" />
        </div>
      </div>

      <!-- Expected costs (at least one): each entry is a period (a single
           year, or an exact date span via the toggle) with an expected cost
           (an exact amount, or a range via the toggle). A multi-year program
           lists one entry per period; entries remove with their X. -->
      <div class="pre-project-form__field">
        <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.costs-label') }} *</label>

        <div v-for="(cost, index) in form.costs" :key="`cost-${index}`" class="pre-project-form__cost">
          <div class="pre-project-form__cost-head">
            <span class="pre-project-form__cost-title">
              {{ $t('page.pre-projects.form.cost-entry', { index: index + 1 }) }}
            </span>
            <BaseRoundButton
              v-if="form.costs.length > 1"
              :size="28"
              is-transparent
              icon="x"
              :tooltip-text="$t('page.pre-projects.form.remove-cost')"
              @click.native="removeCost(index)"
            />
          </div>

          <!-- Period: a single year by default; the toggle switches to a date
               range picker for an exact span. -->
          <div class="pre-project-form__field">
            <div class="pre-project-form__field-head">
              <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.year-label') }} *</label>
              <div class="pre-project-form__toggle">
                <span class="pre-project-form__toggle-label">{{ $t('page.pre-projects.form.date-range-toggle') }}</span>
                <BaseToggle :value="cost.multiYear" is-small @change="onMultiYearToggle(cost, $event)" />
              </div>
            </div>
            <div class="pre-project-form__row">
              <div v-if="!cost.multiYear" class="pre-project-form__field pre-project-form__field--inner">
                <FormInput
                  :name="`pre-project-start-year-${index}`"
                  type="number"
                  :value="cost.startYear"
                  @input="cost.startYear = $event"
                />
              </div>
              <!-- The key re-seeds the picker whenever the stored span changes
                   (prefill, toggle seeding) — it only reads `values` on creation. -->
              <FormDateRangePicker
                v-else
                :key="`pre-project-dates-${index}-${cost.startDate}-${cost.endDate}`"
                class="pre-project-form__dates"
                :name="`pre-project-dates-${index}`"
                :values="selectedDates(cost)"
                hint=""
                @updated-dates="onDatesChange(cost, $event)"
              />
            </div>
          </div>

          <!-- Expected cost: an exact amount by default, optionally a range. -->
          <div class="pre-project-form__field">
            <div class="pre-project-form__field-head">
              <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.cost-label') }} *</label>
              <div class="pre-project-form__toggle">
                <span class="pre-project-form__toggle-label">{{ $t('page.pre-projects.form.cost-range-toggle') }}</span>
                <BaseToggle :value="cost.costRange" is-small @change="onCostRangeToggle(cost, $event)" />
              </div>
            </div>
            <div class="pre-project-form__row">
              <div class="pre-project-form__field pre-project-form__field--inner">
                <label v-if="cost.costRange" class="pre-project-form__field-label">
                  {{ $t('page.pre-projects.form.min') }}
                </label>
                <FormInput
                  :name="`pre-project-cost-min-${index}`"
                  type="number"
                  :value="cost.expectedCostMin"
                  @input="cost.expectedCostMin = $event"
                />
              </div>
              <div v-if="cost.costRange" class="pre-project-form__field pre-project-form__field--inner">
                <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.max') }}</label>
                <FormInput
                  :name="`pre-project-cost-max-${index}`"
                  type="number"
                  :value="cost.expectedCostMax"
                  @input="cost.expectedCostMax = $event"
                />
              </div>
            </div>
          </div>
        </div>

        <BaseButton isSquare isOutlined hasIconLeft isFullWidth @click.native="addCost">
          <BaseIcon name="plus" />
          <span>{{ $t('page.pre-projects.form.add-cost') }}</span>
        </BaseButton>
      </div>

      <div class="pre-project-form__field">
        <label class="pre-project-form__field-label">{{ $t('page.pre-projects.form.sources') }}</label>

        <!-- Saved sources (edit), then sources staged locally in either mode —
             staged ones only persist when the user saves the form. -->
        <ul v-if="sources.length || pendingLinks.length || pendingFiles.length" class="pre-project-form__sources">
          <li
            v-for="source in sources"
            :key="source.id"
            class="pre-project-form__source"
            :class="{ 'is-removed': isPendingRemoval(source) }"
          >
            <BaseIcon :name="source.type === 'LINK' ? 'link' : 'file-text'" class="pre-project-form__source-ico" />
            <span class="pre-project-form__source-name">{{ source.title }}</span>
            <span v-if="isPendingRemoval(source)" class="pre-project-form__source-pending">
              {{ $t('page.pre-projects.form.pending-source') }}
            </span>
            <!-- Staged for deletion: only a restore action until saved. -->
            <BaseRoundButton
              v-if="isPendingRemoval(source)"
              :size="28"
              is-transparent
              icon="rotate-ccw"
              :tooltip-text="$t('actions.cancel')"
              @click.native="restoreSource(source)"
            />
            <template v-else>
              <BaseRoundButton
                :size="28"
                is-transparent
                :icon="source.type === 'LINK' ? 'external-link' : 'download'"
                :tooltip-text="source.type === 'LINK' ? $t('actions.consult') : $t('actions.download')"
                @click.native="openSource(source)"
              />
              <!-- Only the owning tenant's sources are deletable  -->
              <BaseRoundButton
                v-if="source.tenantId"
                :size="28"
                is-transparent
                icon="trash-2"
                :tooltip-text="$t('actions.delete')"
                @click.native="deleteSource(source)"
              />
            </template>
          </li>
          <li v-for="(link, index) in pendingLinks" :key="`pending-link-${index}`" class="pre-project-form__source">
            <BaseIcon name="link" class="pre-project-form__source-ico" />
            <span class="pre-project-form__source-name">{{ link.title || link.url }}</span>
            <span class="pre-project-form__source-pending">{{ $t('page.pre-projects.form.pending-source') }}</span>
            <BaseRoundButton
              :size="28"
              is-transparent
              icon="trash-2"
              :tooltip-text="$t('actions.delete')"
              @click.native="pendingLinks.splice(index, 1)"
            />
          </li>
          <li v-for="(file, index) in pendingFiles" :key="`pending-file-${index}`" class="pre-project-form__source">
            <BaseIcon name="file-text" class="pre-project-form__source-ico" />
            <span class="pre-project-form__source-name">{{ file.name }}</span>
            <span class="pre-project-form__source-pending">{{ $t('page.pre-projects.form.pending-source') }}</span>
            <BaseRoundButton
              :size="28"
              is-transparent
              icon="trash-2"
              :tooltip-text="$t('actions.delete')"
              @click.native="pendingFiles.splice(index, 1)"
            />
          </li>
        </ul>
        <p v-else class="pre-project-form__sources-hint">{{ $t('page.pre-projects.form.no-sources') }}</p>

        <!-- Inline "add link" form (both modes; name optional, url required).
             No confirm action: a valid link stages itself on Enter or when
             focus leaves the row — its trash icon in the list takes it back. -->
        <div v-if="addLinkOpen" class="pre-project-form__row pre-project-form__add-link" @focusout="onLinkRowFocusOut">
          <FormInput
            name="pre-project-source-title"
            :value="linkForm.title"
            :placeholder="$t('page.pre-projects.form.source-title-placeholder')"
            @input="linkForm.title = $event"
            @keyup.native.enter="commitLink"
          />
          <FormInput
            name="pre-project-source-url"
            :value="linkForm.url"
            :placeholder="$t('page.pre-projects.form.source-url-placeholder')"
            @input="linkForm.url = $event"
            @keyup.native.enter="commitLink"
          />
        </div>

        <div class="pre-project-form__source-actions">
          <BaseButton isSquare isOutlined hasIconLeft isFullWidth @click.native="addLinkOpen = !addLinkOpen">
            <BaseIcon name="link" />
            <span>{{ $t('page.pre-projects.form.add-link') }}</span>
          </BaseButton>
        </div>

        <FileUpload
          class="pre-project-form__source-upload"
          display-horizontal
          multiple
          :title="$t('page.pre-projects.form.upload-document')"
          allowed-files=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt"
          @add-files="onSourceFilesAdded"
        />
      </div>
    </template>
    <template slot="actions">
      <div class="pre-project-form__actions">
        <BaseButton isOutlined @click.native="close">{{ $t('page.tenders.views.cancel') }}</BaseButton>
        <!-- Saving disables the button (no double submit) and spins a loader. -->
        <BaseButton
          :disabled="!isValid || submitting"
          :isLoading="submitting"
          :hasIconLeft="submitting"
          @click.native="submit"
        >
          <BaseIcon v-if="submitting" name="loader" />
          <span>{{ $t(`page.pre-projects.form.${isEdit ? 'save-action' : 'create-action'}`) }}</span>
        </BaseButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseChip from '@/components/base/BaseChip.vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import FileUpload from '@/components/form/FileUpload.vue';
import FormDateRangePicker from '@/components/form/FormDateRangePicker.vue';
import FormInput from '@/components/form/FormInput.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import { preProjectSourcesService } from '@/services/pre-project-sources.service';
import {
  LOCATION_LEVEL,
  isSameLocation,
  locationDisplayName,
  locationsOf,
  toLocationPayload,
} from '@/util/location-util';
import { mapActions, mapGetters } from 'vuex';

// The API's costsByYear entries carry ISO dates (e.g. "2026-01-01") while the
// form works in years — extract the year bound from a date on prefill.
const yearFromDate = (date) => {
  if (!date) return null;
  const year = Number(String(date).slice(0, 4));
  return isNaN(year) ? null : year;
};

// One cost entry as the form edits it. Single-year mode edits the year;
// multi-year mode edits the exact ISO span. An exact cost edits only the min.
const emptyCost = () => ({
  id: null,
  multiYear: false,
  startYear: null,
  startDate: null,
  endDate: null,
  costRange: false,
  expectedCostMin: null,
  expectedCostMax: null,
});

// A cost entry as the API lists it (costsByYear) → the form's entry. A span
// covering exactly one calendar year edits as a single year; any other span
// opens in multi-year (date range) mode. A max differing from the min opens
// the range.
const fromApiCost = (cost) => {
  const startDate = cost.startDate || null;
  const endDate = cost.endDate || null;
  const startYear = yearFromDate(startDate);
  const fullSingleYear =
    startDate != null &&
    endDate != null &&
    String(startDate).slice(4) === '-01-01' &&
    String(endDate) === `${startYear}-12-31`;
  const multiYear = startDate != null && endDate != null && !fullSingleYear;
  return {
    // Kept so the edit payload updates the span in place (its createdAt survives).
    id: cost.id || null,
    multiYear,
    startYear: multiYear ? null : startYear,
    startDate,
    endDate,
    costRange: cost.expectedCostMax != null && cost.expectedCostMax !== cost.expectedCostMin,
    expectedCostMin: cost.expectedCostMin != null ? cost.expectedCostMin : null,
    expectedCostMax: cost.expectedCostMax != null ? cost.expectedCostMax : null,
  };
};

// The city select's extra "Other" entry: opens the custom place input.
const OTHER_CITY_ID = '__other__';

const emptyForm = () => ({
  title: '',
  emitter: '',
  // The places the pre-project applies to: { level, locationId, customName }
  // plus the display names (name, regionName, provinceName) for the chips.
  locations: [],
  projectNumber: '',
  // The expected costs, one entry per period (see emptyCost). At least one.
  costs: [emptyCost()],
});

/**
 * Create / edit dialog for custom (tenant-entered) pre-projects. Title, at
 * least one location and at least one expected cost (a period with an amount)
 * are required. A period and an amount are single values by default; the
 * toggles switch them to a date span / a cost range.
 */
export default {
  name: 'PreProjectFormDialog',
  components: {
    BaseButton,
    BaseChip,
    BaseDialog,
    BaseIcon,
    BaseRoundButton,
    BaseToggle,
    FileUpload,
    FormDateRangePicker,
    FormInput,
    FormSelect,
  },
  data() {
    return {
      editedId: null,
      form: emptyForm(),
      // Province and region narrow the lookups (province -> régions -> villes);
      // neither is part of the payload on its own — a city pick carries its own
      // id, and a custom place is tied to the picked region.
      selectedProvinceId: null,
      selectedRegionId: null,
      // "Other" picked in the city select: the custom place input is shown.
      customLocationMode: false,
      customLocationName: '',
      // Sources: saved ones (edit mode) and staged ones. Added links and
      // documents are held locally in both modes and only persist on save —
      // create sends links inline in the payload and uploads documents right
      // after; edit flushes both against the existing pre-project.
      sources: [],
      pendingLinks: [],
      pendingFiles: [],
      // Ids of saved sources staged for deletion (edit mode) — deleted on save.
      pendingRemovals: [],
      addLinkOpen: false,
      linkForm: { title: '', url: '' },
      // Save in flight: the action button shows a spinner and can't re-submit.
      submitting: false,
    };
  },
  computed: {
    ...mapGetters('preProjects', ['cities', 'citiesRegionId', 'regions', 'provinces']),
    isEdit() {
      return !!this.editedId;
    },
    selectedProvince() {
      return this.provinces.find((province) => province.id === this.selectedProvinceId) || null;
    },
    // The regions lookup covers every province; the select only offers the picked one's.
    regionsOfProvince() {
      if (!this.selectedProvinceId) return [];
      return this.regions.filter((region) => region.provinceId === this.selectedProvinceId);
    },
    selectedRegion() {
      return this.regionsOfProvince.find((region) => region.id === this.selectedRegionId) || null;
    },
    otherCityOption() {
      return { id: OTHER_CITY_ID, name: this.$t('page.pre-projects.form.city-other') };
    },
    // The region's cities, then "Other" for a place the catalog doesn't know.
    // Until the picked region's cities have loaded, the store still holds the
    // previous region's list — so the options wait for the matching response.
    cityOptions() {
      if (!this.selectedRegionId) return [];
      const cities = this.citiesRegionId === this.selectedRegionId ? this.cities : [];
      return [...cities, this.otherCityOption];
    },
    isCustomLocationValid() {
      return !!(this.selectedRegionId && this.customLocationName.trim());
    },
    // Only the URL is required on a link source; the name is optional.
    isLinkValid() {
      return !!this.linkForm.url.trim();
    },
    isValid() {
      return !!(
        this.form.title.trim() &&
        this.form.locations.length &&
        this.form.costs.length &&
        this.form.costs.every((cost) => this.isCostValid(cost))
      );
    },
  },
  methods: {
    ...mapActions('preProjects', ['getProvinces', 'getRegions', 'getCities', 'createPreProject', 'updatePreProject']),
    toNumber(value) {
      if (value === null || value === undefined || value === '') return null;
      const number = Number(value);
      return isNaN(number) ? null : number;
    },
    // Open for creation (no argument) or edition (the table row's item).
    open(preProject = null) {
      this.editedId = preProject ? preProject.id : null;
      this.form = emptyForm();
      this.selectedProvinceId = null;
      this.selectedRegionId = null;
      this.customLocationMode = false;
      this.customLocationName = '';
      this.sources = [];
      this.pendingLinks = [];
      this.pendingFiles = [];
      this.pendingRemovals = [];
      this.addLinkOpen = false;
      this.linkForm = { title: '', url: '' };
      this.submitting = false;
      this.$refs.dialog.openModal();
      const lookupsReady = Promise.all([this.getProvinces().catch(() => []), this.getRegions().catch(() => [])]);
      if (preProject) {
        lookupsReady.then(() => this.prefill(preProject));
        this.loadSources();
      }
    },
    // Edit prefill. The locations come with their display names, so they list
    // as chips right away; the province and region selects are pre-scoped to
    // the first location's (resolved by name once the lookups are loaded) so
    // more places from it can be added.
    prefill(preProject) {
      const costs = (preProject.costsByYear || []).map(fromApiCost);
      this.form = {
        title: preProject.title || '',
        emitter: preProject.emitter || '',
        locations: locationsOf(preProject).map((location) => ({ ...location })),
        projectNumber: preProject.projectNumber || '',
        costs: costs.length ? costs : [emptyCost()],
      };

      const first = this.form.locations[0];
      const province = first && this.provinces.find((item) => item.name === first.provinceName);
      if (!province) return;
      this.selectedProvinceId = province.id;
      const region = this.regionsOfProvince.find((item) => item.name === first.regionName);
      if (!region) return;
      this.selectedRegionId = region.id;
      this.getCities({ regionId: region.id }).catch(() => {});
    },
    close() {
      if (this.$refs.dialog) this.$refs.dialog.isModalOpen = false;
    },
    // ---- sources (edit mode) ----
    loadSources() {
      preProjectSourcesService
        .list(this.editedId)
        .then((sources) => {
          this.sources = sources || [];
        })
        .catch(() => {
          this.sources = [];
        });
    },
    // Stage the typed link locally (both modes) — it persists on save: inline
    // in the create payload's `sources`, or flushed to the sources API on edit
    // save. The row clears but stays open so more links can follow.
    commitLink() {
      if (!this.isLinkValid) return;
      // Default to https when the protocol was omitted.
      const raw = this.linkForm.url.trim();
      const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      this.pendingLinks.push({ title: this.linkForm.title.trim() || null, url });
      this.linkForm = { title: '', url: '' };
    },
    // Focus leaving the add-link row entirely commits what was typed (a
    // focusout between the row's own inputs does not).
    onLinkRowFocusOut(event) {
      if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;
      this.commitLink();
    },
    // Stage the dropped files locally (both modes) — they upload on save.
    onSourceFilesAdded(files) {
      const list = Array.from(files || []).filter(Boolean);
      if (!list.length) return;
      this.pendingFiles.push(...list);
    },
    // After an edit save, flush the staged source changes (added links and
    // documents, staged deletions) against the existing pre-project. Failures
    // toast but don't fail the save — the project's fields are already updated.
    persistPendingSources() {
      const additions = [
        ...this.pendingLinks.map((link) => preProjectSourcesService.createLink(this.editedId, link)),
        ...this.pendingFiles.map((file) => preProjectSourcesService.uploadDocument(this.editedId, { file })),
      ];
      const removals = this.pendingRemovals.map((sourceId) => preProjectSourcesService.remove(this.editedId, sourceId));
      if (!additions.length && !removals.length) return Promise.resolve();
      return Promise.all([
        Promise.all(additions).catch((error) =>
          this.toastUploadError(error, this.$t('page.pre-projects.form.upload-failed')),
        ),
        Promise.all(removals).catch((error) => this.$toast.error(error)),
      ]).then(() => this.$emit('sources-changed'));
    },
    // After creation, push the collected documents to the new pre-project.
    // Upload failures toast but don't fail the save — the project exists.
    uploadPendingFiles(created) {
      if (!this.pendingFiles.length) return Promise.resolve(created);
      const preProjectId = created && created.id;
      const uploads = preProjectId
        ? Promise.all(this.pendingFiles.map((file) => preProjectSourcesService.uploadDocument(preProjectId, { file })))
        : Promise.reject(new Error('missing created pre-project id'));
      return uploads.then(
        () => created,
        (error) => {
          this.toastUploadError(error, this.$t('page.pre-projects.form.upload-failed'));
          return created;
        },
      );
    },
    // 409 on a source document upload = a document with the same name already
    // exists on this pre-project; anything else falls back to the given message.
    toastUploadError(error, fallback = null) {
      const isConflict = error.status === 409;
      if (isConflict) {
        this.$toast.error(this.$t('page.pre-projects.form.duplicate-document'));
        return;
      }
      this.$toast.error(fallback || error);
    },
    // A LINK opens its external URL directly; a DOCUMENT resolves its
    // presigned URL first, then navigates to it (no CORS).
    openSource(source) {
      if (source.type === 'LINK') {
        window.open(source.url, '_blank', 'noopener');
        return;
      }
      preProjectSourcesService
        .getDownloadUrl(this.editedId, source.id)
        .then((url) => window.open(url, '_blank', 'noopener'))
        .catch((error) => this.$toast.error(error));
    },
    // Deleting a saved source is staged like the adds — it only hits the API
    // on save. Until then the row shows struck-through with a restore action.
    isPendingRemoval(source) {
      return this.pendingRemovals.includes(source.id);
    },
    deleteSource(source) {
      this.pendingRemovals.push(source.id);
    },
    restoreSource(source) {
      this.pendingRemovals = this.pendingRemovals.filter((id) => id !== source.id);
    },
    // ---- locations ----
    // Picking a province scopes the region select; the region (and any custom
    // place being typed) no longer applies. The picked locations stay as they are.
    onProvinceSelected(province) {
      this.selectedProvinceId = province ? province.id : null;
      this.selectedRegionId = null;
      this.resetCustomLocation();
    },
    // Picking a region loads its cities.
    onRegionSelected(region) {
      this.selectedRegionId = region ? region.id : null;
      this.resetCustomLocation();
      if (this.selectedRegionId) this.getCities({ regionId: this.selectedRegionId }).catch(() => {});
    },
    // A picked city joins the list (once) as a CITY entry, carrying the names
    // its chip displays. "Other" opens the custom place input instead.
    onCitySelected(city) {
      if (!city) return;
      if (city.id === OTHER_CITY_ID) {
        this.customLocationMode = true;
        return;
      }
      this.resetCustomLocation();
      this.addLocation({
        level: LOCATION_LEVEL.CITY,
        locationId: city.id,
        name: city.name,
        customName: null,
        regionName: this.selectedRegion ? this.selectedRegion.name : null,
        provinceName: this.provinceNameOfSelection(),
      });
    },
    // The typed custom place joins the list as a REGION entry carrying its
    // customName — an unknown place is always tied to at least its region.
    commitCustomLocation() {
      if (!this.isCustomLocationValid) return;
      const region = this.selectedRegion;
      this.addLocation({
        level: LOCATION_LEVEL.REGION,
        locationId: this.selectedRegionId,
        name: region ? region.name : null,
        customName: this.customLocationName.trim(),
        regionName: region ? region.name : null,
        provinceName: this.provinceNameOfSelection(),
      });
      this.resetCustomLocation();
    },
    resetCustomLocation() {
      this.customLocationMode = false;
      this.customLocationName = '';
    },
    provinceNameOfSelection() {
      if (this.selectedProvince) return this.selectedProvince.name;
      return this.selectedRegion ? this.selectedRegion.province || null : null;
    },
    addLocation(location) {
      if (this.form.locations.some((item) => isSameLocation(item, location))) return;
      this.form.locations.push(location);
    },
    removeLocation(index) {
      this.form.locations.splice(index, 1);
    },
    locationKey(location) {
      return `${location.level}-${location.locationId}-${location.customName || ''}`;
    },
    // Chip text: the place, then its region — e.g. "Lévis · Chaudière-Appalaches",
    // or "Halifax · Halifax" (a city can share its region's name). A whole-region
    // entry is just the region. The province shows on hover (same as the table).
    locationLabel(location) {
      const name = locationDisplayName(location);
      const isRegion = location.level === LOCATION_LEVEL.REGION && !location.customName;
      const region = !isRegion && location.regionName ? location.regionName : null;
      return [name, region].filter(Boolean).join(' · ');
    },
    // The chip's hover text: the province name — none when the entry IS the province.
    locationTooltip(location) {
      if (!location.provinceName) return null;
      if (location.level === LOCATION_LEVEL.PROVINCE && !location.customName) return null;
      return location.provinceName;
    },
    // ---- costs ----
    addCost() {
      this.form.costs.push(emptyCost());
    },
    // The last entry can't be removed — a pre-project carries at least one cost.
    removeCost(index) {
      if (this.form.costs.length <= 1) return;
      this.form.costs.splice(index, 1);
    },
    // The range picker's model for an entry: the picked span, without null bounds.
    selectedDates(cost) {
      return [cost.startDate, cost.endDate].filter(Boolean);
    },
    // Single-year mode needs the year; multi-year needs the full picked span,
    // in order (ISO dates compare lexically). The cost needs its min (and, as
    // a range, a max at least the min).
    isCostValid(cost) {
      const costMin = this.toNumber(cost.expectedCostMin);
      const costMax = cost.costRange ? this.toNumber(cost.expectedCostMax) : costMin;
      const periodValid = cost.multiYear
        ? !!(cost.startDate && cost.endDate && cost.startDate <= cost.endDate)
        : this.toNumber(cost.startYear) != null;
      return periodValid && costMin != null && costMax != null && costMax >= costMin;
    },
    // Multi-year switches the single year to an exact date range: toggling on
    // seeds the range from the typed year (re-seeding when the year changed
    // since the range was last set); toggling off keeps its start year.
    onMultiYearToggle(cost, enabled) {
      cost.multiYear = enabled;
      const startYear = this.toNumber(cost.startYear);
      if (enabled) {
        if (startYear != null && (!cost.startDate || yearFromDate(cost.startDate) !== startYear)) {
          cost.startDate = `${startYear}-01-01`;
          cost.endDate = `${startYear}-12-31`;
        }
      } else if (startYear == null) {
        cost.startYear = yearFromDate(cost.startDate);
      }
    },
    // The picker applies a sorted [start, end]; a single pick leaves the end
    // open, keeping the form invalid until the span is complete.
    onDatesChange(cost, dates) {
      const [startDate = null, endDate = null] = dates || [];
      cost.startDate = startDate;
      cost.endDate = endDate;
    },
    onCostRangeToggle(cost, enabled) {
      cost.costRange = enabled;
      if (!enabled) cost.expectedCostMax = null;
    },
    // An entry as the create/update payload carries it. The API expects ISO
    // dates: a single year spans Jan 1 to Dec 31; multi-year sends the picked
    // range as-is. An exact cost mirrors the value into both bounds.
    costToPayload(cost) {
      const startYear = this.toNumber(cost.startYear);
      const costMin = this.toNumber(cost.expectedCostMin);
      return {
        // A span sent with its id is updated in place; without one it is inserted.
        ...(cost.id ? { id: cost.id } : {}),
        startDate: cost.multiYear ? cost.startDate : `${startYear}-01-01`,
        endDate: cost.multiYear ? cost.endDate : `${startYear}-12-31`,
        expectedCostMin: costMin,
        expectedCostMax: cost.costRange ? this.toNumber(cost.expectedCostMax) : costMin,
      };
    },
    submit() {
      if (this.submitting) return;
      // Flush what was typed but not yet added — Save can be reached without
      // the add-link row losing focus or the custom place's "Add" being clicked.
      // The place joins the locations first, so it counts toward validity.
      this.commitCustomLocation();
      this.commitLink();
      if (!this.isValid) return;
      this.submitting = true;
      const preProject = {
        title: this.form.title.trim(),
        emitter: this.form.emitter.trim() || null,
        locations: this.form.locations.map(toLocationPayload),
        projectNumber: this.form.projectNumber.trim() || null,
        costs: this.form.costs.map((cost) => this.costToPayload(cost)),
      };
      const request = this.isEdit
        ? // Staged sources (links and documents) only persist now, on save.
          this.updatePreProject({ preProjectId: this.editedId, preProject }).then(() => this.persistPendingSources())
        : // Pending link sources travel inline with the create payload; pending
          // documents upload against the created pre-project's id right after.
          this.createPreProject({ ...preProject, sources: this.pendingLinks }).then((created) =>
            this.uploadPendingFiles(created),
          );
      request.then(
        () => {
          this.submitting = false;
          this.$emit('saved', { isEdit: this.isEdit });
          this.close();
        },
        (error) => {
          this.submitting = false;
          this.$toast.error(error);
        },
      );
    },
  },
};
</script>

<style lang="scss">
.pre-project-form {
  &__field {
    flex: 1;
    margin-bottom: $space-md;
    min-width: 0;

    // Nested inside another field (the year/cost rows) — the outer field
    // already provides the bottom spacing.
    &--inner {
      margin-bottom: 0;
    }

    // City stays inert until a region is picked (FormSelect has no disabled prop).
    &.is-disabled {
      opacity: 0.6;
      pointer-events: none;
    }
  }

  // The picked locations, as removable chips above the region/city pickers.
  &__locations {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: $space-2;
  }

  &__locations-hint {
    font-size: $font-size-xsmall;
    color: $color-slate-500;
    margin: 0 0 $space-2;
  }

  // The custom place input with its add button beside it.
  &__custom-location {
    display: flex;
    align-items: center;
    gap: $space-2;

    .form-input {
      flex: 1;
      min-width: 0;
      padding: 0;
    }
  }

  &__field-label {
    display: block;
    font-size: $font-size-xsmall;
    font-weight: 800;
    color: $color-normal;
    margin-bottom: 6px;
  }

  // FormInput carries a form-layout padding scaffold we don't need inside the modal.
  &__field .form-input {
    padding: 0;
  }

  &__field-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-3;
  }

  &__toggle {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__toggle-label {
    font-size: $font-size-xsmall;
    color: $color-slate-500;
  }

  &__row {
    display: flex;
    gap: $space-3;

    .form-input {
      flex: 1;
      min-width: 0;
      padding: 0;
    }
  }

  // One expected-cost entry (period + amount), boxed so several read as a list.
  &__cost {
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    padding: $space-3;
    margin-bottom: $space-3;

    // The entry's last field sits on the box edge.
    > .pre-project-form__field:last-child {
      margin-bottom: 0;
    }
  }

  &__cost-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 28px;
    margin-bottom: $space-2;
  }

  &__cost-title {
    font-size: $font-size-xsmall;
    font-weight: 800;
    color: $color-slate-500;
    text-transform: uppercase;
  }

  // Consecutive rows inside one field (the location pickers): the inner fields
  // carry no bottom margin, so the row itself keeps the standard field spacing.
  &__row--stacked {
    margin-bottom: $space-md;
  }

  // Multi-year mode: the shared date range picker replaces the year input.
  // Trim its form-layout scaffold (own label + fixed height) — the field
  // already carries the "Year" label in its head row.
  &__dates.date-range-picker {
    height: auto;

    > .form-label {
      display: none;
    }

    // No format hint under the field — the picked range is already shown in it.
    .v-text-field__details {
      display: none;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  // ---- sources (edit mode) ----
  &__sources {
    list-style: none;
    margin: 0 0 $space-2;
    padding: 0;
  }

  &__source {
    display: flex;
    align-items: center;
    gap: $space-2;
    padding: $space-1 0;

    // Staged for deletion (applies on save).
    &.is-removed .pre-project-form__source-name {
      color: $color-slate-500;
      text-decoration: line-through;
    }
  }

  &__source-ico {
    width: 14px;
    height: 14px;
    flex: none;
    color: $color-slate-500;
  }

  &__source-name {
    flex: 1;
    min-width: 0;
    font-size: $font-size-small;
    color: $color-normal;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Marks a staged (not yet saved) source row.
  &__source-pending {
    flex: none;
    font-size: $font-size-xsmall;
    font-style: italic;
    color: $color-slate-500;
  }

  &__sources-hint {
    font-size: $font-size-xsmall;
    color: $color-slate-500;
    margin: 0 0 $space-2;
  }

  &__add-link {
    align-items: center;
    margin-bottom: $space-2;
  }

  &__source-actions {
    display: flex;
    align-items: center;
    gap: $space-2;
    margin-bottom: $space-2;
  }
}

// Centered popup (BaseDialog defaults to a right-side drawer) — same override
// as the view dialogs.
.base-dialog__pre-project-form {
  position: relative;
  top: 0;
  height: auto;
  width: 520px;
  max-width: 90vw;

  .base-dialog__header {
    height: auto;
    border-bottom: 1px solid $color-border;

    .header__title {
      font-size: $font-size-bigger;
      margin: 18px 22px;
    }
  }

  .base-dialog__header + .base-dialog__content {
    padding: $space-5 22px;
    background: $color-white;
  }

  // The province/region/city dropdown menus: FormSelect defaults to min-width
  // 340px / max-height 400px, which overflows the half-width fields and the dialog.
  .form-select__menu {
    min-width: 0;
    max-height: 220px;
  }

  .base-dialog__actions {
    padding: $space-md 22px;
    border-top: 1px solid $color-border;
  }
}
</style>
